import { computed, inject, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { useToaster } from 'app/shared/composables/use-toaster';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { SiteId } from 'app/site-detail/models/Site';

import { VideoCallSessionStore } from '../video-call-session.store';
import { VideoCallsStore } from '../video-calls.store';

import type { VideoCallButtonStateView } from './use-video-call-button-state';

export type VideoCallAnalyticsSource = 'Installation' | 'Alert resolution' | 'Toast';

export interface UseVideoCallOptions {
    analyticsSource: VideoCallAnalyticsSource;
}

export function useVideoCallWorkflow(
    siteId: Signal<SiteId | undefined>,
    vm: Signal<VideoCallButtonStateView | undefined>,
    options: UseVideoCallOptions,
) {
    const analyticsService = inject(AnalyticsService);
    const translate = inject(TranslateService);
    const toaster = useToaster();
    const videoCalls = inject(VideoCallsStore);
    const session = inject(VideoCallSessionStore);

    const analyticsSource = options.analyticsSource;
    const loading = signal(false);
    const error = signal<unknown | null>(null);
    const invitationConfirmationSiteId = signal<SiteId | undefined>(undefined);

    const invitationConfirmationOpen = computed(() => !!invitationConfirmationSiteId());

    const closeInvitationConfirmation = () => {
        invitationConfirmationSiteId.set(undefined);
    };

    const runVideoCallAction = async (event: () => Promise<void>) => {
        if (loading()) return false;

        loading.set(true);
        error.set(null);

        try {
            await event();
            return true;
        } catch (e) {
            error.set(e);
            toaster.error({
                title: translate.instant('ERROR_SITES_TAKES_TO_LONG_TO_LOAD_OR_UNAVAILABLE'),
                duration: 15_000,
            });
            return false;
        } finally {
            loading.set(false);
        }
    };

    const startVideoCall = async (id: SiteId) => {
        await videoCalls.invite(id);
        analyticsService.videoCallInvitationSent(analyticsSource);
    };

    const resendInvitation = async (data: Extract<VideoCallButtonStateView, { action: 'RESEND_INVITATION' }>) => {
        await videoCalls.resend(data.videoCall);
    };

    const joinVideoCall = async (data: Extract<VideoCallButtonStateView, { action: 'JOIN_VIDEO_CALL' }>) => {
        await session.join(data.videoCall);
        analyticsService.videoCallJoined(analyticsSource);
    };

    const stopAndJoinVideoCall = async (
        data: Extract<VideoCallButtonStateView, { action: 'STOP_AND_JOIN_VIDEO_CALL' }>,
    ) => {
        await session.stopAndJoin(data.videoCall);
        analyticsService.videoCallStoppedAndJoined(analyticsSource);
    };

    const confirmInvitation = async () => {
        const id = invitationConfirmationSiteId();
        closeInvitationConfirmation();

        if (!id) return false;

        return runVideoCallAction(() => startVideoCall(id));
    };

    const handleAction = async () => {
        const id = siteId();
        const data = vm();

        if (!id || !data || data.action === 'NONE') return false;

        switch (data.action) {
            case 'APPROVE_SEND_INVITATION':
                if (!data.owner) return false;
                invitationConfirmationSiteId.set(id);
                return true;
            case 'RESEND_INVITATION':
                if (!data.owner) return false;
                return runVideoCallAction(() => resendInvitation(data));
            case 'JOIN_VIDEO_CALL':
                return runVideoCallAction(() => joinVideoCall(data));
            case 'STOP_AND_JOIN_VIDEO_CALL':
                return runVideoCallAction(() => stopAndJoinVideoCall(data));
        }
    };

    return {
        loading: loading.asReadonly(),
        error: error.asReadonly(),
        invitationConfirmationOpen,
        closeInvitationConfirmation,
        confirmInvitation,
        handleAction,
    };
}
